import logger from './controllers/logger';
import { addGameObject, getAbsolutePosAndScale, getEntityState } from './entity';

import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageClearRect,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import { ObjectType } from './shared/api_shared_types';

import { VisibilityState, RenderItemType, Shape } from './types';
import type * as t from './types';

export function controller(): t.GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
    dispose,
  };
}

function dispose(obj: t.DOMObject) {
  obj.wrapperEl?.remove();
  for (let i = obj.elements.length - 1; i >= 0; i--) {
    obj.elements[i].remove();
  }
}

export function createDomObject(
  ent: t.Entity,
  content: HTMLElement | HTMLElement[],
  htmlParent: HTMLElement,
  manager: t.GameManager,
): t.DOMObject {
  let wrapperElement = document.createElement('div') as HTMLDivElement;
  wrapperElement.classList.add('entityDomWrapper');
  wrapperElement.style.display = 'none';

  const contentElements: HTMLElement[] = [];

  if (content) {
    if (Array.isArray(content)) {
      contentElements.push(...content);
    } else {
      contentElements.push(content);
    }

    for (const contentElement of contentElements) {
      wrapperElement.appendChild(contentElement);
      contentElement.style.position = 'absolute';
    }
  }

  htmlParent.append(wrapperElement);

  let newDomObj: t.DOMObject = {
    ...ent,
    shape: Shape.RECT,
    objectType: ObjectType.DOM_ELEMENT,
    wrapperEl: wrapperElement,
    elements: contentElements,
    defaultUpdate: false,
    centerContent: false,
    cornerRadius: 0,
    contentWidth: 0,
    contentHeight: 0,
    contentMatchWidth: false,
    contentMatchHeight: false,
    contentAspect: 1,
    clearContent: false,
    shapeStyle: null,
    domZIndex: 0,
    currentWrapperElStyle: { x: 0, y: 0, width: 0, height: 0, zIndex: 0, scale: 1 },
    currentContentElStyle: { top: 0, left: 0, width: 0, height: 0, zIndex: 0 },
  };

  addGameObject(manager, newDomObj);

  return newDomObj;
}

function update(obj: t.DOMObject): boolean {
  const wrapperElement = obj.wrapperEl;
  const contentElement = obj.elements;

  const state = getEntityState(obj);

  if (state !== VisibilityState.HIDDEN && wrapperElement.style.display !== '') {
    wrapperElement.style.display = '';
  } else if (state === VisibilityState.HIDDEN && wrapperElement.style.display !== 'none') {
    wrapperElement.style.display = 'none';
  }

  const [pos, scale] = getAbsolutePosAndScale(obj);

  //Wrapper Dev Element
  const wWidth = obj.width;
  const wHeight = obj.height;
  const x = pos.x - wWidth / 2;
  const y = pos.y - wHeight / 2;
  const zIndex = obj.domZIndex;

  let doUpdateTransform = false;

  if (obj.currentWrapperElStyle.scale !== scale) {
    obj.currentWrapperElStyle.scale = scale;
    doUpdateTransform = true;
  }
  if (obj.currentWrapperElStyle.x !== x) {
    obj.currentWrapperElStyle.x = x;
    doUpdateTransform = true;
  }
  if (obj.currentWrapperElStyle.y !== y) {
    obj.currentWrapperElStyle.y = y;
    doUpdateTransform = true;
  }
  if (obj.currentWrapperElStyle.width !== wWidth) {
    wrapperElement.style.width = wWidth + 'px';
    obj.currentWrapperElStyle.width = wWidth;
  }
  if (obj.currentWrapperElStyle.height !== wHeight) {
    wrapperElement.style.height = wHeight + 'px';
    obj.currentWrapperElStyle.height = wHeight;
  }

  if (doUpdateTransform) {
    wrapperElement.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  }

  if (obj.currentWrapperElStyle.zIndex !== zIndex) {
    wrapperElement.style.zIndex = zIndex.toString();
    obj.currentWrapperElStyle.zIndex = zIndex;
  }

  if (obj.contentAspect && (obj.contentMatchWidth || obj.contentMatchHeight)) {
    if (obj.contentMatchWidth) {
      obj.contentWidth = obj.width;
      if (obj.contentMatchHeight) {
        obj.contentHeight = obj.height;
      } else {
        obj.contentHeight = obj.contentWidth / obj.contentAspect;
      }
    } else {
      obj.contentHeight = obj.height;
      obj.contentWidth = obj.contentHeight * obj.contentAspect;
    }
  }

  //Content Div Element
  if (obj.contentWidth > 0 || obj.contentHeight > 0) {
    for (const contentElement of obj.elements) {
      const cWidth = obj.contentWidth;
      const cHeight = obj.contentHeight;

      if (obj.currentContentElStyle.width !== cWidth) {
        contentElement.style.width = cWidth + 'px';
        obj.currentContentElStyle.width = cWidth;
      }
      if (obj.currentContentElStyle.height !== cHeight) {
        contentElement.style.height = cHeight + 'px';
        obj.currentContentElStyle.height = cHeight;
      }

      if (obj.centerContent) {
        let cLeft = 0;
        if (wWidth !== cWidth) {
          cLeft = -(cWidth - wWidth) / 2;
        }
        if (obj.currentContentElStyle.left !== cLeft) {
          contentElement.style.left = cLeft + 'px';
          obj.currentContentElStyle.left = cLeft;
        }
        let cTop = 0;
        if (wHeight !== cHeight) {
          cTop = -(cHeight - wHeight) / 2;
        }
        if (obj.currentContentElStyle.top !== cTop) {
          contentElement.style.top = cTop + 'px';
          obj.currentContentElStyle.top = cTop;
        }
      }
    }
  }

  const cornerRadius = obj.cornerRadius + 'px';
  if (wrapperElement.style.borderRadius !== cornerRadius) {
    wrapperElement.style.borderRadius = obj.cornerRadius + 'px';
  }

  for (const contentElement of obj.elements) {
    if (contentElement.style.borderRadius !== cornerRadius) {
      contentElement.style.borderRadius = obj.cornerRadius + 'px';
    }
  }

  return false;
}

export function createPackage(
  domObj: t.DOMObject,
  position: t.V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const w = domObj.width;
  if (domObj.clearContent) {
    let cRect: RenderPackageClearRect = {
      type: RenderItemType.clearRect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x + (-w / 2) * scale,
      positionY: position.y + (-domObj.height / 2) * scale,
      width: w,
      height: domObj.height,
      roundedCorners: true,
      cornerRadius1: domObj.cornerRadius,
      cornerRadius2: domObj.cornerRadius,
      cornerRadius3: domObj.cornerRadius,
      cornerRadius4: domObj.cornerRadius,
    };
    rPackage.push(cRect);
  }

  if (domObj.shapeStyle) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: w,
      height: domObj.height,
      shapeStyle: domObj.shapeStyle,
      shadow: null,
      cornerRadius1: domObj.cornerRadius,
      cornerRadius2: domObj.cornerRadius,
      cornerRadius3: domObj.cornerRadius,
      cornerRadius4: domObj.cornerRadius,
    };
    rPackage.push(rect);
  }

  return rPackage;
}
